/**
 * Pure helpers for drag-to-reorder columns, kept free of React/DOM so the
 * splice logic is unit-testable (same split as keyboardNav.ts).
 */

/**
 * Reconcile a (possibly stale) persisted order against the current leaf
 * column ids: drop ids that no longer exist, append new ids in definition
 * order. Mirrors how TanStack orders columns missing from `columnOrder`
 * (listed ids first, remainder appended), so the order we compute moves
 * against is exactly the order on screen.
 */
export function resolveColumnOrder(
  order: readonly string[],
  leafIds: readonly string[]
): string[] {
  const present = new Set(leafIds);
  const kept = order.filter((id) => present.has(id));
  const seen = new Set(kept);
  return [...kept, ...leafIds.filter((id) => !seen.has(id))];
}

/**
 * Move `draggedId` adjacent to `targetId`, landing on the far side in the
 * direction of travel (dragging right places it right of the target;
 * dragging left places it left) — the AG grid / scout DataGrid semantics.
 * Returns null when either id is unknown or nothing would move.
 */
export function moveColumn(
  order: readonly string[],
  draggedId: string,
  targetId: string
): string[] | null {
  if (draggedId === targetId) return null;
  const from = order.indexOf(draggedId);
  const to = order.indexOf(targetId);
  if (from === -1 || to === -1) return null;
  const next = [...order];
  next.splice(from, 1);
  next.splice(to, 0, draggedId);
  return next;
}

/** Which edge of `targetId` the drop indicator marks while dragging. */
export function dropIndicatorSide(
  order: readonly string[],
  draggedId: string,
  targetId: string
): "left" | "right" {
  return order.indexOf(draggedId) < order.indexOf(targetId) ? "right" : "left";
}
